import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useCloudExport } from '../editor/cloudExport';
import { receiveExportJob } from '../editor/exportHandoff';
import { localVideoBlocker, runVideoExport, videoSize, type ExportJob, type VideoPreview } from '../editor/videoExport';
import './export.css';

/**
 * The page a video export runs in (Tarek, 3 Nov: "a separate page that opens when I press export
 * video that says rendering, keep this page opened, with the option to render on the cloud").
 *
 * The editor opens it and hands it the frozen file (`exportHandoff.ts`); it draws and encodes in a
 * worker on this machine (`videoExport.ts`), which carries on while the page is behind other tabs,
 * and saves the file when it is done. The render service is one press away, and is where the
 * export goes on its own when this browser cannot make the video.
 *
 * The look is the "Ring" direction Tarek picked (3 Nov): a calm light page, the frame being drawn
 * now sitting inside a progress ring, one line of what is happening, and pill buttons. When it is
 * done the ring closes and the picture inside it plays the finished file.
 */

type Phase =
  | { kind: 'waiting' }
  | { kind: 'lost'; message: string }
  | { kind: 'local'; fraction: number; message: string; gpu?: boolean }
  | { kind: 'cloud'; reason?: string }
  | { kind: 'done'; where: 'local' | 'cloud'; seconds: number }
  | { kind: 'failed'; message: string }
  | { kind: 'cancelled' };

function save(bytes: Uint8Array, name: string, type: string): void {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function duration(seconds: number): string {
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
}

/** The ring's size, and the circle inside it the picture has to fit in. */
const RING = 320;
const STROKE = 6;
const INSIDE = RING - 44;

/** The largest box of this width/height ratio that fits inside a circle of `diameter`. */
function inscribed(ratio: number, diameter: number): { width: number; height: number } {
  const height = diameter / Math.sqrt(1 + ratio * ratio);
  return { width: Math.round(height * ratio), height: Math.round(height) };
}

/** Draws a bitmap into a canvas, filling it. */
function Frame({ bitmap, className }: { bitmap: ImageBitmap | null; className: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !bitmap) return;
    if (canvas.width !== bitmap.width || canvas.height !== bitmap.height) {
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
    }
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0);
  }, [bitmap]);
  return <canvas ref={ref} className={className} aria-hidden />;
}

export function ExportRoute() {
  const { documentId = '' } = useParams();
  const [params] = useSearchParams();
  const jobId = params.get('job') ?? '';
  const [job, setJob] = useState<ExportJob | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'waiting' });
  const [cover, setCover] = useState<string | null>(null);
  const [live, setLive] = useState<{ bitmap: ImageBitmap; target: number } | null>(null);
  const [result, setResult] = useState<{ url: string; video: boolean } | null>(null);
  const started = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const made = useRef<{ bytes: Uint8Array; name: string; type: string } | null>(null);
  const cloud = useCloudExport(documentId);

  // Only the newest frame is shown, so each one closes the one before it.
  const onPreview = useCallback((preview: VideoPreview) => {
    setLive((was) => {
      if (was && was.bitmap !== preview.bitmap) was.bitmap.close();
      return { bitmap: preview.bitmap, target: preview.target };
    });
  }, []);

  const renderHere = useCallback(async (given: ExportJob) => {
    const blocker = await localVideoBlocker(given.snapshot).catch((error: Error) => error.message);
    if (blocker) return goCloud(given, `${blocker} It is being rendered in the cloud instead.`);
    const abort = new AbortController();
    controller.current = abort;
    started.current = Date.now();
    setResult(null);
    setPhase({ kind: 'local', fraction: 0, message: 'Starting…' });
    try {
      const out = await runVideoExport(given.snapshot, abort.signal, (progress) => setPhase({ kind: 'local', ...progress }), onPreview);
      const single = out.files === 1;
      const type = single ? (given.snapshot.format === 'mp4' ? 'video/mp4' : 'image/gif') : 'application/zip';
      made.current = { bytes: out.bytes, name: given.fileName, type };
      save(out.bytes, given.fileName, type);
      // The finished file, played inside the ring (for a zip of several there is no one file to play).
      if (single) setResult({ url: URL.createObjectURL(new Blob([out.bytes as BlobPart], { type })), video: given.snapshot.format === 'mp4' });
      setPhase({ kind: 'done', where: 'local', seconds: (Date.now() - started.current) / 1000 });
    } catch (error) {
      if (abort.signal.aborted) return;
      setPhase({ kind: 'failed', message: (error as Error).message });
    } finally {
      if (controller.current === abort) controller.current = null;
    }
  }, [onPreview]); // eslint-disable-line react-hooks/exhaustive-deps

  const goCloud = useCallback((given: ExportJob, reason?: string) => {
    controller.current?.abort();
    controller.current = null;
    started.current = Date.now();
    setPhase({ kind: 'cloud', ...(reason ? { reason } : {}) });
    void cloud.start(given.snapshot.format, given.cloud.targets, given.cloud.zipName, given.cloud.scale);
  }, [cloud]);

  // The job, from the editor that opened this page.
  useEffect(() => {
    let alive = true;
    receiveExportJob(jobId).then(
      (given) => {
        if (!alive) return;
        setJob(given);
        if (given.cover) setCover(URL.createObjectURL(new Blob([given.cover as BlobPart], { type: 'image/png' })));
        void renderHere(given);
      },
      (error: Error) => alive && setPhase({ kind: 'lost', message: error.message }),
    );
    return () => { alive = false; };
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The render service's answer, as this page's own phase.
  useEffect(() => {
    if (phase.kind !== 'cloud' || !cloud.state) return;
    if (cloud.state.status === 'READY') setPhase({ kind: 'done', where: 'cloud', seconds: (Date.now() - started.current) / 1000 });
    else if (cloud.state.status === 'FAILED') setPhase({ kind: 'failed', message: cloud.state.error ?? 'The cloud render failed.' });
    else if (cloud.state.status === 'CANCELLED') setPhase({ kind: 'cancelled' });
  }, [cloud.state, phase.kind]);

  const working = phase.kind === 'local' || phase.kind === 'cloud';
  const fraction = phase.kind === 'local' ? phase.fraction : phase.kind === 'cloud' ? (cloud.state?.progress ?? 0) / 100 : phase.kind === 'done' ? 1 : 0;
  const percent = Math.round(fraction * 100);
  const targets = job?.snapshot.targets ?? [];
  const current = live?.target ?? 0;
  const target = targets[current] ?? targets[0];
  const name = targets.length === 1 ? target!.name.replace(/\.(mp4|gif)$/i, '') : job ? `${targets.length} artboards` : 'your video';

  // Closing the page stops a render on this machine: the browser asks first.
  useEffect(() => {
    if (phase.kind !== 'local') return undefined;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [phase.kind]);

  useEffect(() => {
    document.title = working ? `${percent}% · Rendering ${name}` : phase.kind === 'done' ? `Rendered · ${name}` : `Export · ${job?.documentName ?? 'Artwork'}`;
  }, [working, percent, name, phase.kind, job]);

  const left = useMemo(() => {
    if (!working || fraction < 0.04 || !started.current) return null;
    const spent = (Date.now() - started.current) / 1000;
    return duration((spent / fraction) * (1 - fraction));
  }, [fraction, working]);

  const size = target && job ? videoSize(target.box, job.snapshot.scale) : null;
  const ratio = target ? Math.max(0.1, Math.min(10, target.box.width / target.box.height)) : 4 / 5;
  const box = inscribed(ratio, INSIDE);
  const fps = job?.snapshot.fps ?? 30;
  const where = phase.kind === 'local' && phase.gpu === false ? 'this computer' : 'this computer’s graphics card';
  // Percent and time left sit in their own fixed-width slots, so nothing around them moves as they change.
  const readout = working ? `${percent}%` : phase.kind === 'done' ? '100%' : '';

  const title = (() => {
    switch (phase.kind) {
      case 'waiting': return 'Getting your file…';
      case 'lost': return 'This export can’t be found';
      case 'local':
      case 'cloud': return `Rendering ${name}`;
      case 'done': return `${name} is ready`;
      case 'failed': return 'The render stopped';
      case 'cancelled': return 'Render cancelled';
    }
  })();

  const status = (() => {
    switch (phase.kind) {
      case 'waiting': return 'Fetching it from the editor.';
      case 'lost': return phase.message;
      case 'local': return `On ${where}. Switch tabs if you like, it keeps going.`;
      case 'cloud': return phase.reason ?? 'In the cloud. You can close this page, it will be in your exports.';
      case 'done': return `Rendered ${phase.where === 'local' ? 'on this computer' : 'in the cloud'} in ${duration(phase.seconds)} and saved to your Downloads.`;
      case 'failed': return phase.message;
      case 'cancelled': return 'Nothing was saved. You can start it again below.';
    }
  })();

  const circumference = 100;
  const arc = phase.kind === 'done' ? circumference : Math.max(working ? 1.5 : 0, fraction * circumference);

  return (
    <div className="ring-export" data-phase={phase.kind}>
      <main className="ring-export__center">
        <div className="ring-export__dial" style={{ width: RING, height: RING }}>
          <svg className="ring-export__ring" width={RING} height={RING} viewBox={`0 0 ${RING} ${RING}`} aria-hidden>
            <circle className="ring-export__track" cx={RING / 2} cy={RING / 2} r={(RING - STROKE) / 2} strokeWidth={STROKE} />
            {phase.kind === 'waiting' ? (
              <circle className="ring-export__spin" cx={RING / 2} cy={RING / 2} r={(RING - STROKE) / 2} strokeWidth={STROKE} pathLength={circumference} strokeDasharray={`18 ${circumference}`} />
            ) : (
              <circle
                className="ring-export__arc"
                cx={RING / 2}
                cy={RING / 2}
                r={(RING - STROKE) / 2}
                strokeWidth={STROKE}
                pathLength={circumference}
                strokeDasharray={`${arc} ${circumference}`}
                transform={`rotate(-90 ${RING / 2} ${RING / 2})`}
              />
            )}
          </svg>
          <div className="ring-export__picture" style={{ width: box.width, height: box.height }}>
            {phase.kind === 'done' && result ? (
              result.video
                ? <video className="ring-export__media" src={result.url} autoPlay loop muted playsInline />
                : <img className="ring-export__media" src={result.url} alt={name} />
            ) : live ? (
              <Frame bitmap={live.bitmap} className="ring-export__media" />
            ) : cover ? (
              <img className="ring-export__media ring-export__media--waiting" src={cover} alt="" />
            ) : null}
          </div>
        </div>

        <div className="ring-export__words" aria-live="polite">
          <h1 className="ring-export__title" role="status" title={title}>{title}</h1>
          <p className="ring-export__status" data-testid="export-state">{status}</p>
          <p className="ring-export__meta">
            <span className="ring-export__progress">{readout ? <><strong>{readout}</strong>{working ? ` · ${left ? `about ${left} left` : 'starting…'}` : ''}</> : null}</span>
            <span className="ring-export__specs">{size ? `${size.width} × ${size.height} · ${fps} fps · ${job!.snapshot.format.toUpperCase()}${targets.length > 1 ? ` · artboard ${current + 1} of ${targets.length}` : ''}` : ''}</span>
          </p>
        </div>

        <div className="ring-export__buttons">
          {phase.kind === 'local' && job ? (
            <>
              <button type="button" className="ring-export__pill" onClick={() => goCloud(job)}>Render in the cloud instead</button>
              <button type="button" className="ring-export__pill ring-export__pill--quiet" onClick={() => { controller.current?.abort(); setPhase({ kind: 'cancelled' }); }}>Cancel</button>
            </>
          ) : null}
          {phase.kind === 'cloud' ? (
            <button type="button" className="ring-export__pill ring-export__pill--quiet" onClick={() => void cloud.cancel()}>Cancel</button>
          ) : null}
          {phase.kind === 'done' ? (
            <>
              <button type="button" className="ring-export__pill ring-export__pill--primary" onClick={() => (made.current ? save(made.current.bytes, made.current.name, made.current.type) : void cloud.again())}>Download again</button>
              <button type="button" className="ring-export__pill ring-export__pill--quiet" onClick={() => window.close()}>Close</button>
            </>
          ) : null}
          {(phase.kind === 'failed' || phase.kind === 'cancelled') && job ? (
            <>
              <button type="button" className="ring-export__pill ring-export__pill--primary" onClick={() => void renderHere(job)}>Render on this computer</button>
              <button type="button" className="ring-export__pill" onClick={() => goCloud(job)}>Render in the cloud</button>
            </>
          ) : null}
        </div>
      </main>
      <footer className="ring-export__foot">{job?.documentName ?? ''}</footer>
    </div>
  );
}
